import React, { useState } from 'react';
import { BellIcon, ShieldIcon, KeyIcon, GlobeIcon, CreditCardIcon, UserIcon, MailIcon, SmartphoneIcon, CheckIcon, XIcon, PlusIcon, TrashIcon, EyeIcon, EyeOffIcon, AlertCircleIcon } from 'lucide-react';
type TabType = 'profile' | 'billing' | 'privacy' | 'security' | 'notifications';
export const Settings = () => {
  const [activeTab, setActiveTab] = useState<TabType>('profile');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  // Profile state
  const [profileData, setProfileData] = useState({
    displayName: 'John Doe',
    email: 'john@example.com',
    phone: '+1 (555) 123-4567',
    bio: 'AI enthusiast and developer',
    location: 'San Francisco, CA',
    website: 'https://johndoe.com'
  });
  // Privacy state
  const [privacySettings, setPrivacySettings] = useState({
    profileVisibility: 'public',
    showEmail: false,
    showApps: true,
    allowMessages: true,
    dataSharing: false,
    analytics: true
  });
  // Security state
  const [securitySettings, setSecuritySettings] = useState({
    twoFactorEnabled: false,
    loginAlerts: true,
    sessionTimeout: '30'
  });
  // Notification state
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    appUpdates: true,
    newFeatures: true,
    marketingEmails: false,
    weeklyDigest: true,
    securityAlerts: true
  });
  const tabs = [{
    id: 'profile' as TabType,
    label: 'Profile',
    icon: <UserIcon className="h-5 w-5" data-id="element-2214" />
  }, {
    id: 'billing' as TabType,
    label: 'Billing',
    icon: <CreditCardIcon className="h-5 w-5" data-id="element-2215" />
  }, {
    id: 'privacy' as TabType,
    label: 'Privacy',
    icon: <ShieldIcon className="h-5 w-5" data-id="element-2216" />
  }, {
    id: 'security' as TabType,
    label: 'Security',
    icon: <KeyIcon className="h-5 w-5" data-id="element-2217" />
  }, {
    id: 'notifications' as TabType,
    label: 'Notifications',
    icon: <BellIcon className="h-5 w-5" data-id="element-2218" />
  }];
  const paymentMethods = [{
    id: '1',
    type: 'Visa',
    last4: '4242',
    expiry: '12/25',
    isDefault: true
  }, {
    id: '2',
    type: 'Mastercard',
    last4: '8888',
    expiry: '09/24',
    isDefault: false
  }];
  const billingHistory = [{
    id: '1',
    date: 'June 1, 2024',
    amount: '$9.99',
    status: 'Paid',
    invoice: 'INV-001'
  }, {
    id: '2',
    date: 'May 1, 2024',
    amount: '$9.99',
    status: 'Paid',
    invoice: 'INV-002'
  }, {
    id: '3',
    date: 'April 1, 2024',
    amount: '$9.99',
    status: 'Paid',
    invoice: 'INV-003'
  }];
  const activeSessions = [{
    id: '1',
    device: 'Chrome on MacBook Pro',
    location: 'San Francisco, CA',
    lastActive: '2 minutes ago',
    current: true
  }, {
    id: '2',
    device: 'Safari on iPhone 14',
    location: 'San Francisco, CA',
    lastActive: '1 hour ago',
    current: false
  }];
  return <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8" data-id="element-2219">
      <div className="max-w-6xl mx-auto" data-id="element-2220">
        <h1 className="text-2xl font-bold mb-8" data-id="element-2221">Settings</h1>
        <div className="flex flex-col lg:flex-row gap-6" data-id="element-2222">
          {/* Sidebar Navigation */}
          <div className="lg:w-64 flex-shrink-0" data-id="element-2223">
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-2224">
              {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full flex items-center px-4 py-3 border-b border-gray-200 last:border-b-0 transition-colors ${activeTab === tab.id ? 'bg-indigo-50 text-indigo-600' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-2225">
                  {tab.icon}
                  <span className="ml-3 font-medium" data-id="element-2226">{tab.label}</span>
                </button>)}
            </div>
          </div>
          {/* Content Area */}
          <div className="flex-1" data-id="element-2227">
            {/* Profile Tab */}
            {activeTab === 'profile' && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2228">
                <h2 className="text-xl font-semibold mb-6" data-id="element-2229">Profile Settings</h2>
                <div className="space-y-6" data-id="element-2230">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2231">
                    <div data-id="element-2232">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2233">
                        Display Name
                      </label>
                      <input type="text" value={profileData.displayName} onChange={e => setProfileData({
                    ...profileData,
                    displayName: e.target.value
                  })} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-2234" />
                    </div>
                    <div data-id="element-2235">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2236">
                        Email Address
                      </label>
                      <input type="email" value={profileData.email} onChange={e => setProfileData({
                    ...profileData,
                    email: e.target.value
                  })} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-2237" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2238">
                    <div data-id="element-2239">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2240">
                        Phone Number
                      </label>
                      <input type="tel" value={profileData.phone} onChange={e => setProfileData({
                    ...profileData,
                    phone: e.target.value
                  })} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-2241" />
                    </div>
                    <div data-id="element-2242">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2243">
                        Location
                      </label>
                      <input type="text" value={profileData.location} onChange={e => setProfileData({
                    ...profileData,
                    location: e.target.value
                  })} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-2244" />
                    </div>
                  </div>
                  <div data-id="element-2245">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2246">
                      Website
                    </label>
                    <input type="url" value={profileData.website} onChange={e => setProfileData({
                  ...profileData,
                  website: e.target.value
                })} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-2247" />
                  </div>
                  <div data-id="element-2248">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2249">
                      Bio
                    </label>
                    <textarea value={profileData.bio} onChange={e => setProfileData({
                  ...profileData,
                  bio: e.target.value
                })} rows={4} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-2250" />
                  </div>
                  <div className="flex justify-end space-x-4 pt-4" data-id="element-2251">
                    <button className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2252">
                      Cancel
                    </button>
                    <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700" data-id="element-2253">
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>}
            {/* Billing Tab */}
            {activeTab === 'billing' && <div className="space-y-6" data-id="element-2254">
                {/* Payment Methods */}
                <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2255">
                  <div className="flex justify-between items-center mb-6" data-id="element-2256">
                    <h2 className="text-xl font-semibold" data-id="element-2257">Payment Methods</h2>
                    <button className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700" data-id="element-2258">
                      <PlusIcon className="h-4 w-4 mr-2" data-id="element-2259" />
                      Add Method
                    </button>
                  </div>
                  <div className="space-y-4" data-id="element-2260">
                    {paymentMethods.map(method => <div key={method.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg" data-id="element-2261">
                        <div className="flex items-center" data-id="element-2262">
                          <CreditCardIcon className="h-6 w-6 text-gray-400 mr-4" data-id="element-2263" />
                          <div data-id="element-2264">
                            <div className="font-medium" data-id="element-2265">
                              {method.type} •••• {method.last4}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-2266">
                              Expires {method.expiry}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2" data-id="element-2267">
                          {method.isDefault && <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full" data-id="element-2268">
                              Default
                            </span>}
                          <button className="p-2 text-gray-400 hover:text-red-600" data-id="element-2269">
                            <TrashIcon className="h-4 w-4" data-id="element-2270" />
                          </button>
                        </div>
                      </div>)}
                  </div>
                </div>
                {/* Billing History */}
                <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2271">
                  <h2 className="text-xl font-semibold mb-6" data-id="element-2272">
                    Billing History
                  </h2>
                  <div className="overflow-x-auto" data-id="element-2273">
                    <table className="w-full" data-id="element-2274">
                      <thead data-id="element-2275">
                        <tr className="border-b border-gray-200" data-id="element-2276">
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-700" data-id="element-2277">
                            Date
                          </th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-700" data-id="element-2278">
                            Amount
                          </th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-700" data-id="element-2279">
                            Status
                          </th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-700" data-id="element-2280">
                            Invoice
                          </th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-gray-700" data-id="element-2281">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody data-id="element-2282">
                        {billingHistory.map(item => <tr key={item.id} className="border-b border-gray-100" data-id="element-2283">
                            <td className="py-3 px-4 text-sm" data-id="element-2284">{item.date}</td>
                            <td className="py-3 px-4 text-sm font-medium" data-id="element-2285">
                              {item.amount}
                            </td>
                            <td className="py-3 px-4" data-id="element-2286">
                              <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full" data-id="element-2287">
                                {item.status}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-500" data-id="element-2288">
                              {item.invoice}
                            </td>
                            <td className="py-3 px-4 text-right" data-id="element-2289">
                              <button className="text-sm text-indigo-600 hover:text-indigo-700" data-id="element-2290">
                                Download
                              </button>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>}
            {/* Privacy Tab */}
            {activeTab === 'privacy' && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2291">
                <h2 className="text-xl font-semibold mb-6" data-id="element-2292">Privacy Settings</h2>
                <div className="space-y-6" data-id="element-2293">
                  <div data-id="element-2294">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2295">
                      Profile Visibility
                    </label>
                    <select value={privacySettings.profileVisibility} onChange={e => setPrivacySettings({
                  ...privacySettings,
                  profileVisibility: e.target.value
                })} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-2296">
                      <option value="public" data-id="element-2297">Public</option>
                      <option value="private" data-id="element-2298">Private</option>
                      <option value="friends" data-id="element-2299">Friends Only</option>
                    </select>
                    <p className="mt-1 text-sm text-gray-500" data-id="element-2300">
                      Control who can see your profile
                    </p>
                  </div>
                  <div className="space-y-4 pt-4 border-t border-gray-200" data-id="element-2301">
                    <label className="flex items-center justify-between" data-id="element-2302">
                      <div data-id="element-2303">
                        <div className="font-medium" data-id="element-2304">Show Email Address</div>
                        <div className="text-sm text-gray-500" data-id="element-2305">
                          Display your email on your profile
                        </div>
                      </div>
                      <button onClick={() => setPrivacySettings({
                    ...privacySettings,
                    showEmail: !privacySettings.showEmail
                  })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${privacySettings.showEmail ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-2306">
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${privacySettings.showEmail ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-2307" />
                      </button>
                    </label>
                    <label className="flex items-center justify-between" data-id="element-2308">
                      <div data-id="element-2309">
                        <div className="font-medium" data-id="element-2310">Show My Apps</div>
                        <div className="text-sm text-gray-500" data-id="element-2311">
                          Display your created apps publicly
                        </div>
                      </div>
                      <button onClick={() => setPrivacySettings({
                    ...privacySettings,
                    showApps: !privacySettings.showApps
                  })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${privacySettings.showApps ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-2312">
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${privacySettings.showApps ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-2313" />
                      </button>
                    </label>
                    <label className="flex items-center justify-between" data-id="element-2314">
                      <div data-id="element-2315">
                        <div className="font-medium" data-id="element-2316">Allow Messages</div>
                        <div className="text-sm text-gray-500" data-id="element-2317">
                          Let other users send you messages
                        </div>
                      </div>
                      <button onClick={() => setPrivacySettings({
                    ...privacySettings,
                    allowMessages: !privacySettings.allowMessages
                  })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${privacySettings.allowMessages ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-2318">
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${privacySettings.allowMessages ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-2319" />
                      </button>
                    </label>
                    <label className="flex items-center justify-between" data-id="element-2320">
                      <div data-id="element-2321">
                        <div className="font-medium" data-id="element-2322">Data Sharing</div>
                        <div className="text-sm text-gray-500" data-id="element-2323">
                          Share usage data to improve services
                        </div>
                      </div>
                      <button onClick={() => setPrivacySettings({
                    ...privacySettings,
                    dataSharing: !privacySettings.dataSharing
                  })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${privacySettings.dataSharing ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-2324">
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${privacySettings.dataSharing ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-2325" />
                      </button>
                    </label>
                    <label className="flex items-center justify-between" data-id="element-2326">
                      <div data-id="element-2327">
                        <div className="font-medium" data-id="element-2328">Analytics</div>
                        <div className="text-sm text-gray-500" data-id="element-2329">
                          Help us improve with anonymous analytics
                        </div>
                      </div>
                      <button onClick={() => setPrivacySettings({
                    ...privacySettings,
                    analytics: !privacySettings.analytics
                  })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${privacySettings.analytics ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-2330">
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${privacySettings.analytics ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-2331" />
                      </button>
                    </label>
                  </div>
                  <div className="flex justify-end space-x-4 pt-4" data-id="element-2332">
                    <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700" data-id="element-2333">
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>}
            {/* Security Tab */}
            {activeTab === 'security' && <div className="space-y-6" data-id="element-2334">
                {/* Change Password */}
                <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2335">
                  <h2 className="text-xl font-semibold mb-6" data-id="element-2336">
                    Change Password
                  </h2>
                  <div className="space-y-4" data-id="element-2337">
                    <div data-id="element-2338">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2339">
                        Current Password
                      </label>
                      <div className="relative" data-id="element-2340">
                        <input type={showPassword ? 'text' : 'password'} className="w-full border border-gray-300 rounded-md px-4 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-2341" />
                        <button onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-2.5 text-gray-400" data-id="element-2342">
                          {showPassword ? <EyeOffIcon className="h-5 w-5" data-id="element-2343" /> : <EyeIcon className="h-5 w-5" data-id="element-2344" />}
                        </button>
                      </div>
                    </div>
                    <div data-id="element-2345">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2346">
                        New Password
                      </label>
                      <div className="relative" data-id="element-2347">
                        <input type={showNewPassword ? 'text' : 'password'} className="w-full border border-gray-300 rounded-md px-4 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-2348" />
                        <button onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-2.5 text-gray-400" data-id="element-2349">
                          {showNewPassword ? <EyeOffIcon className="h-5 w-5" data-id="element-2350" /> : <EyeIcon className="h-5 w-5" data-id="element-2351" />}
                        </button>
                      </div>
                    </div>
                    <div data-id="element-2352">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2353">
                        Confirm New Password
                      </label>
                      <input type="password" className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-2354" />
                    </div>
                    <div className="flex justify-end pt-2" data-id="element-2355">
                      <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700" data-id="element-2356">
                        Update Password
                      </button>
                    </div>
                  </div>
                </div>
                {/* Two-Factor Authentication */}
                <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2357">
                  <h2 className="text-xl font-semibold mb-6" data-id="element-2358">
                    Two-Factor Authentication
                  </h2>
                  <div className="space-y-4" data-id="element-2359">
                    <label className="flex items-center justify-between" data-id="element-2360">
                      <div data-id="element-2361">
                        <div className="font-medium" data-id="element-2362">Enable 2FA</div>
                        <div className="text-sm text-gray-500" data-id="element-2363">
                          Add an extra layer of security to your account
                        </div>
                      </div>
                      <button onClick={() => setSecuritySettings({
                    ...securitySettings,
                    twoFactorEnabled: !securitySettings.twoFactorEnabled
                  })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${securitySettings.twoFactorEnabled ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-2364">
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${securitySettings.twoFactorEnabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-2365" />
                      </button>
                    </label>
                    {securitySettings.twoFactorEnabled && <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-2366">
                        <div className="flex items-start" data-id="element-2367">
                          <AlertCircleIcon className="h-5 w-5 text-blue-600 mr-3 mt-0.5" data-id="element-2368" />
                          <div data-id="element-2369">
                            <p className="text-sm text-blue-900 font-medium" data-id="element-2370">
                              2FA is enabled
                            </p>
                            <p className="text-sm text-blue-700 mt-1" data-id="element-2371">
                              You will need to enter a verification code when
                              logging in
                            </p>
                          </div>
                        </div>
                      </div>}
                  </div>
                </div>
                {/* Security Preferences */}
                <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2372">
                  <h2 className="text-xl font-semibold mb-6" data-id="element-2373">
                    Security Preferences
                  </h2>
                  <div className="space-y-4" data-id="element-2374">
                    <label className="flex items-center justify-between" data-id="element-2375">
                      <div data-id="element-2376">
                        <div className="font-medium" data-id="element-2377">Login Alerts</div>
                        <div className="text-sm text-gray-500" data-id="element-2378">
                          Get notified of new login attempts
                        </div>
                      </div>
                      <button onClick={() => setSecuritySettings({
                    ...securitySettings,
                    loginAlerts: !securitySettings.loginAlerts
                  })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${securitySettings.loginAlerts ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-2379">
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${securitySettings.loginAlerts ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-2380" />
                      </button>
                    </label>
                    <div data-id="element-2381">
                      <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2382">
                        Session Timeout
                      </label>
                      <select value={securitySettings.sessionTimeout} onChange={e => setSecuritySettings({
                    ...securitySettings,
                    sessionTimeout: e.target.value
                  })} className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-2383">
                        <option value="15" data-id="element-2384">15 minutes</option>
                        <option value="30" data-id="element-2385">30 minutes</option>
                        <option value="60" data-id="element-2386">1 hour</option>
                        <option value="120" data-id="element-2387">2 hours</option>
                      </select>
                      <p className="mt-1 text-sm text-gray-500" data-id="element-2388">
                        Automatically log out after inactivity
                      </p>
                    </div>
                  </div>
                </div>
                {/* Active Sessions */}
                <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2389">
                  <h2 className="text-xl font-semibold mb-6" data-id="element-2390">
                    Active Sessions
                  </h2>
                  <div className="space-y-4" data-id="element-2391">
                    {activeSessions.map(session => <div key={session.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg" data-id="element-2392">
                        <div className="flex items-center" data-id="element-2393">
                          <SmartphoneIcon className="h-6 w-6 text-gray-400 mr-4" data-id="element-2394" />
                          <div data-id="element-2395">
                            <div className="font-medium flex items-center" data-id="element-2396">
                              {session.device}
                              {session.current && <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full" data-id="element-2397">
                                  Current
                                </span>}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-2398">
                              {session.location} • {session.lastActive}
                            </div>
                          </div>
                        </div>
                        {!session.current && <button className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-md" data-id="element-2399">
                            Revoke
                          </button>}
                      </div>)}
                  </div>
                </div>
              </div>}
            {/* Notifications Tab */}
            {activeTab === 'notifications' && <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2400">
                <h2 className="text-xl font-semibold mb-6" data-id="element-2401">
                  Notification Preferences
                </h2>
                <div className="space-y-4" data-id="element-2402">
                  <label className="flex items-center justify-between" data-id="element-2403">
                    <div data-id="element-2404">
                      <div className="font-medium" data-id="element-2405">Email Notifications</div>
                      <div className="text-sm text-gray-500" data-id="element-2406">
                        Receive notifications via email
                      </div>
                    </div>
                    <button onClick={() => setNotificationSettings({
                  ...notificationSettings,
                  emailNotifications: !notificationSettings.emailNotifications
                })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${notificationSettings.emailNotifications ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-2407">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notificationSettings.emailNotifications ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-2408" />
                    </button>
                  </label>
                  <label className="flex items-center justify-between" data-id="element-2409">
                    <div data-id="element-2410">
                      <div className="font-medium" data-id="element-2411">App Updates</div>
                      <div className="text-sm text-gray-500" data-id="element-2412">
                        Get notified when your apps are updated
                      </div>
                    </div>
                    <button onClick={() => setNotificationSettings({
                  ...notificationSettings,
                  appUpdates: !notificationSettings.appUpdates
                })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${notificationSettings.appUpdates ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-2413">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notificationSettings.appUpdates ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-2414" />
                    </button>
                  </label>
                  <label className="flex items-center justify-between" data-id="element-2415">
                    <div data-id="element-2416">
                      <div className="font-medium" data-id="element-2417">New Features</div>
                      <div className="text-sm text-gray-500" data-id="element-2418">
                        Learn about new platform features
                      </div>
                    </div>
                    <button onClick={() => setNotificationSettings({
                  ...notificationSettings,
                  newFeatures: !notificationSettings.newFeatures
                })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${notificationSettings.newFeatures ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-2419">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notificationSettings.newFeatures ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-2420" />
                    </button>
                  </label>
                  <label className="flex items-center justify-between" data-id="element-2421">
                    <div data-id="element-2422">
                      <div className="font-medium" data-id="element-2423">Marketing Emails</div>
                      <div className="text-sm text-gray-500" data-id="element-2424">
                        Receive promotional content and offers
                      </div>
                    </div>
                    <button onClick={() => setNotificationSettings({
                  ...notificationSettings,
                  marketingEmails: !notificationSettings.marketingEmails
                })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${notificationSettings.marketingEmails ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-2425">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notificationSettings.marketingEmails ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-2426" />
                    </button>
                  </label>
                  <label className="flex items-center justify-between" data-id="element-2427">
                    <div data-id="element-2428">
                      <div className="font-medium" data-id="element-2429">Weekly Digest</div>
                      <div className="text-sm text-gray-500" data-id="element-2430">
                        Get a weekly summary of your activity
                      </div>
                    </div>
                    <button onClick={() => setNotificationSettings({
                  ...notificationSettings,
                  weeklyDigest: !notificationSettings.weeklyDigest
                })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${notificationSettings.weeklyDigest ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-2431">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notificationSettings.weeklyDigest ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-2432" />
                    </button>
                  </label>
                  <label className="flex items-center justify-between" data-id="element-2433">
                    <div data-id="element-2434">
                      <div className="font-medium" data-id="element-2435">Security Alerts</div>
                      <div className="text-sm text-gray-500" data-id="element-2436">
                        Important security notifications
                      </div>
                    </div>
                    <button onClick={() => setNotificationSettings({
                  ...notificationSettings,
                  securityAlerts: !notificationSettings.securityAlerts
                })} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${notificationSettings.securityAlerts ? 'bg-indigo-600' : 'bg-gray-200'}`} data-id="element-2437">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notificationSettings.securityAlerts ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-2438" />
                    </button>
                  </label>
                  <div className="flex justify-end space-x-4 pt-4" data-id="element-2439">
                    <button className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700" data-id="element-2440">
                      Save Preferences
                    </button>
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </div>
    </div>;
};